import React, { useState } from 'react';
import {
  Activity, Heart, Shield, HeartPulse, Droplet, Pill, Wind, Bone, Brain, Syringe,
  ArrowRight, Search, Upload, UserCheck, DollarSign, CreditCard, ShieldCheck, Plane,
  MessageCircle, Phone, Star, TrendingUp, Award, Users, FileCheck, CheckCircle2,
  Clock, Package, CalendarDays, TrendingDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Testimonials from '../components/Testimonials';
import FAQ from '../components/FAQ';
import { useAdminContext } from '../context/AdminContext';

const PharmacyHomePage = () => {
  const { siteData } = useAdminContext();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    email: '',
    phone: '',
    condition: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const iconMap = {
    Activity, Heart, Shield, HeartPulse, Droplet, Pill, Wind, Bone, Brain, Syringe,
    Search, Upload, UserCheck, DollarSign, CreditCard, ShieldCheck, Plane
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${siteData.contact.whatsapp}`, '_blank');
  };

  const handleCTAClick = (target) => {
    if (!target) return;

    if (target.toLowerCase() === 'whatsapp') {
      handleWhatsApp();
    } else if (target.startsWith('#')) {
      const element = document.querySelector(target);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (target.startsWith('http')) {
      window.open(target, '_blank');
    } else {
      navigate(target);
    }
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    // Use dynamic target if available, else fallback to standard category link
    const target = siteData.categories.labels?.viewMedsTarget || `/category/${categoryId}`;
    handleCTAClick(target);
  };

  const handlePrescriptionUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPrescriptionFile(file);
      toast.success(`Prescription "${file.name}" uploaded successfully!`);
    }
  };

  const handleConsultationSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('name', consultationForm.name);
      formData.append('email', consultationForm.email);
      formData.append('phone', consultationForm.phone);
      formData.append('condition', consultationForm.condition);
      formData.append('message', consultationForm.message);

      if (prescriptionFile) {
        // Convert to base64
        const fileBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(prescriptionFile);
        });
        formData.append('fileData', fileBase64);
        formData.append('mimeType', prescriptionFile.type || 'application/octet-stream');
        formData.append('fileName', prescriptionFile.name);
      }

      await fetch('https://script.google.com/macros/s/AKfycbwvAZy0VD6ZFiQ9WeGceewPRwCGXKL8kORAtARIIeTY-kcnh2NURjOYHnvJ4YASRNAzZA/exec', {
        method: 'POST',
        body: formData,
        mode: 'no-cors' // Use no-cors to avoid preflight issues for simple submissions
      });

      toast.success('Consultation request submitted! Our expert will contact you within 4 hours.');
      setConsultationForm({ name: '', email: '', phone: '', condition: '', message: '' });
      setPrescriptionFile(null);
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit consultation request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md border-b border-red-100">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-2xl">{siteData.header.logoLabel}</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">{siteData.header.logoTitle}</h1>
                <p className="text-xs text-gray-600 font-medium">{siteData.header.logoSubtitle}</p>
              </div>
            </div>

            <nav className="hidden lg:flex items-center space-x-8">
              {siteData.header.navLinks.map((link, idx) => (
                <button key={idx} onClick={() => handleCTAClick(link.target)} className="text-gray-700 hover:text-red-600 font-medium transition-colors">
                  {link.label}
                </button>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <Button onClick={() => handleCTAClick(siteData.header.ctaWhatsAppTarget || 'whatsapp')} className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-lg font-semibold flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all">
                <MessageCircle className="w-4 h-4" />
                <span>{siteData.header.ctaWhatsApp}</span>
              </Button>
              <Button onClick={() => handleCTAClick(siteData.header.ctaExpertTarget || '#consultation')} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-lg font-semibold flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all">
                <Phone className="w-4 h-4" />
                <span>{siteData.header.ctaExpert}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-red-50 via-white to-red-50 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(220, 38, 38) 1px, transparent 0)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Main Hero Content */}
            <div className="text-center space-y-8 mb-16">
              <div className="inline-flex items-center space-x-2 bg-white rounded-full px-6 py-3 shadow-lg border-2 border-red-200">
                {iconMap[siteData.hero.badgeIcon] && React.createElement(iconMap[siteData.hero.badgeIcon], { className: "w-5 h-5 text-red-600" })}
                <span className="text-sm font-bold text-gray-800">{siteData.hero.badgeText}</span>
              </div>

              <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 leading-tight">
                {siteData.hero.title.split(' ').map((word, i) =>
                  word.includes('%') ? <span key={i} className="text-red-600">{word} </span> : word + ' '
                )}
              </h1>

              <p className="text-xl lg:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
                {siteData.hero.subtitle}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
                <Button onClick={() => handleCTAClick(siteData.hero.primaryCTATarget || '#categories')} className="bg-red-600 hover:bg-red-700 text-white px-10 py-6 text-lg rounded-xl font-bold shadow-2xl hover:shadow-red-300 hover:scale-105 transition-all flex items-center justify-center space-x-3">
                  <span>{siteData.hero.primaryCTAText}</span>
                  {iconMap[siteData.hero.primaryCTAIcon] && React.createElement(iconMap[siteData.hero.primaryCTAIcon], { className: "w-6 h-6" })}
                </Button>
                <Button onClick={() => handleCTAClick(siteData.hero.secondaryCTATarget || '#consultation')} variant="outline" className="border-3 border-red-600 text-red-600 hover:bg-red-50 px-10 py-6 text-lg rounded-xl font-bold hover:scale-105 transition-all flex items-center justify-center space-x-3">
                  {iconMap[siteData.hero.secondaryCTAIcon] && React.createElement(iconMap[siteData.hero.secondaryCTAIcon], { className: "w-6 h-6" })}
                  <span>{siteData.hero.secondaryCTAText}</span>
                </Button>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {siteData.hero.stats.map((stat, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-6 shadow-xl border-2 border-red-100 hover:scale-105 transition-transform">
                  <p className="text-4xl font-bold text-red-600 mb-2">{stat.value}</p>
                  <p className="text-gray-700 font-semibold text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Disease Categories Section */}
      <section id="categories" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              {siteData.categories.title.split(' ').map((word, i) =>
                word.toLowerCase() === 'category' || word.toLowerCase() === 'disease' ? <span key={i} className="text-red-600">{word} </span> : word + ' '
              )}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {siteData.categories.subtitle}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {siteData.categories.list.map((category) => {
              const Icon = iconMap[category.icon] || Activity;
              return (
                <button
                  key={category.id}
                  onClick={() => handleCategorySelect(category.id)}
                  className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-gray-200 hover:border-red-400 relative overflow-hidden"
                >
                  {/* Background Image */}
                  {category.image && (
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300">
                      <img src={category.image} alt={category.name} className="w-full h-full object-cover" />
                    </div>
                  )}

                  {/* Type Badge */}
                  <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-bold ${category.type === 'terminal' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                    {category.type === 'terminal' ? (siteData.categories.labels?.terminalBadge || 'Terminal') : (siteData.categories.labels?.chronicBadge || 'Chronic')}
                  </div>

                  <div className="relative z-10">
                    {/* Icon */}
                    <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-6 transition-transform shadow-lg">
                      <Icon className="w-8 h-8 text-white" />
                    </div>

                    {/* Category Name */}
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors">
                      {category.name}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                      {category.description}
                    </p>

                    {/* Stats */}
                    <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-100">
                      <div className="flex flex-col">
                        <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{siteData.categories.labels?.productsAvailable || 'Products'}</span>
                        <span className="text-sm font-bold text-red-600">{category.productCount || '50+'}</span>
                      </div>
                      <div className="w-px h-8 bg-gray-200" />
                      <div className="flex flex-col">
                        <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{siteData.categories.labels?.avgSavings || 'Savings'}</span>
                        <span className="text-sm font-bold text-green-600">{category.avgSavings || '70%'}</span>
                      </div>
                    </div>

                    {/* Popular Meds */}
                    {category.commonMeds && (
                      <div className="mb-6">
                        <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-2">{siteData.categories.labels?.popularMeds || 'Popular:'}</span>
                        <div className="flex flex-wrap gap-1">
                          {category.commonMeds.map((med, i) => (
                            <span key={i} className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md font-medium">
                              {med}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Arrow */}
                    <div className="flex items-center text-red-600 font-bold text-sm group-hover:translate-x-2 transition-transform">
                      <span>{siteData.categories.labels?.viewMeds || 'Inquire Now'}</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cost Savings Comparison Section */}
      <section id="savings" className="py-20 bg-gradient-to-br from-red-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-16">
              <div className="inline-flex items-center space-x-2 bg-white rounded-full px-6 py-3 shadow-lg border-2 border-red-200 mb-4">
                {iconMap[siteData.savings.badgeIcon] && React.createElement(iconMap[siteData.savings.badgeIcon], { className: "w-5 h-5 text-green-600" })}
                <span className="text-sm font-bold text-gray-800">{siteData.savings.badgeText}</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
                {siteData.savings.title.split(' ').map((word, i) =>
                  word.toLowerCase().includes('indian') || word.toLowerCase().includes('generic') ? <span key={i} className="text-red-600">{word} </span> : word + ' '
                )}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {siteData.savings.subtitle}
              </p>
            </div>

            {/* Comparison Cards */}
            <div className="space-y-6">
              {siteData.savings.list.map((item, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 overflow-hidden hover:shadow-2xl transition-all">
                  <div className="grid md:grid-cols-12 gap-0">
                    {/* Category */}
                    <div className="md:col-span-2 bg-gradient-to-br from-red-600 to-red-700 p-6 flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-white font-bold text-lg">{item.category}</p>
                      </div>
                    </div>

                    {/* Medication */}
                    <div className="md:col-span-3 p-6 border-r border-gray-200">
                      <p className="text-sm text-gray-600 mb-2">{siteData.savings.labels?.medicationCol || 'Medication'}</p>
                      <p className="font-bold text-gray-900 text-lg">{item.medication}</p>
                    </div>

                    {/* US Price */}
                    <div className="md:col-span-2 p-6 bg-red-50 border-r border-gray-200">
                      <p className="text-sm text-gray-600 mb-2">{siteData.savings.labels?.usPriceCol || 'US Price'}</p>
                      <p className="font-bold text-red-600 text-2xl">{item.usPrice}</p>
                      <p className="text-xs text-gray-500">{siteData.savings.labels?.perMonthText || 'per month'}</p>
                    </div>

                    {/* Indian Price */}
                    <div className="md:col-span-2 p-6 bg-green-50 border-r border-gray-200">
                      <p className="text-sm text-gray-600 mb-2">{siteData.savings.labels?.ourPriceCol || 'Our Price'}</p>
                      <p className="font-bold text-green-600 text-2xl">{item.indianPrice}</p>
                      <p className="text-xs text-gray-500">{siteData.savings.labels?.perMonthText || 'per month'}</p>
                    </div>

                    {/* Savings */}
                    <div className="md:col-span-3 p-6 bg-gradient-to-r from-green-50 to-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600 mb-2">{siteData.savings.labels?.youSaveCol || 'You Save'}</p>
                          <p className="font-bold text-green-600 text-2xl">{item.savings}</p>
                          <p className="text-xs text-gray-500">{siteData.savings.labels?.perMonthText || 'per month'}</p>
                        </div>
                        <div className="text-right">
                          <div className="bg-green-600 text-white rounded-full px-4 py-2 font-bold text-lg">
                            {item.savingsPercent}
                          </div>
                          <p className="text-xs text-gray-600 mt-2">{siteData.savings.labels?.annualText || 'Annual:'} {item.annualUSSavings}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-12 text-center">
              <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-8 text-white shadow-2xl">
                <h3 className="text-3xl font-bold mb-4">{siteData.savings.ctaTitle}</h3>
                <p className="text-xl text-red-100 mb-6">{siteData.savings.ctaSubtitle}</p>
                <Button onClick={() => handleCTAClick(siteData.savings.ctaTarget || '#consultation')} className="bg-white text-red-600 hover:bg-gray-100 px-10 py-4 text-lg rounded-xl font-bold shadow-xl hover:scale-105 transition-all">
                  {siteData.savings.ctaButton}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="trust" className="py-20 bg-gray-50 border-y border-red-100 italic">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-12 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
            {siteData.legacy.partnerships.map((partner, idx) => (
              <span key={idx} className="text-2xl font-black tracking-tighter text-gray-400">{partner}</span>
            ))}
          </div>
        </div>
      </section>

      <Testimonials siteData={siteData} />

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gradient-to-br from-red-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              {siteData.howItWorks.title.split(' ').map((word, i) =>
                word.toLowerCase().includes('works') ? <span key={i} className="text-red-600">{word} </span> : word + ' '
              )}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {siteData.howItWorks.subtitle}
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="relative">
              {/* Connection Line */}
              <div className="hidden lg:block absolute top-20 left-0 right-0 h-1 bg-gradient-to-r from-red-200 via-red-600 to-red-200"></div>

              <div className="grid md:grid-cols-7 gap-6">
                {siteData.howItWorks.steps.map((step, index) => {
                  const Icon = iconMap[step.icon] || CheckCircle2;
                  return (
                    <div key={step.step} className="relative">
                      <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-200 hover:shadow-2xl hover:-translate-y-2 transition-all relative z-10">
                        {/* Step Number */}
                        <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 text-white rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                          {step.step}
                        </div>

                        {/* Icon */}
                        <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center mb-4 mx-auto">
                          <Icon className="w-8 h-8 text-red-600" />
                        </div>

                        {/* Title */}
                        <h3 className="text-center font-bold text-gray-900 mb-3 text-sm">{step.title}</h3>

                        {/* Description */}
                        <p className="text-center text-xs text-gray-600 leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Legacy & Trust Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
                {siteData.legacy.title.split(' ').map((word, i) =>
                  word.toLowerCase().includes('years') ? <span key={i} className="text-red-600">{word} </span> : word + ' '
                )}
              </h2>
              <p className="text-xl text-gray-600">
                {siteData.legacy.subtitle}
              </p>
            </div>

            {/* Timeline */}
            <div className="relative mb-16">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-red-200 via-red-600 to-red-200"></div>

              <div className="space-y-12">
                {siteData.legacy.timeline.map((milestone, index) => (
                  <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                    <div className="w-5/12"></div>
                    <div className="w-2/12 flex justify-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center text-white font-bold shadow-xl z-10">
                        {milestone.year}
                      </div>
                    </div>
                    <div className="w-5/12">
                      <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-100 hover:shadow-xl transition-all">
                        <p className="text-gray-900 font-semibold">{milestone.event}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Certifications & Partnerships */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 shadow-xl border-2 border-red-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <ShieldCheck className="w-8 h-8 text-red-600 mr-3" />
                  {siteData.legacy.certificationsTitle || 'Certifications'}
                </h3>
                <div className="space-y-3">
                  {siteData.legacy.certifications.map((cert, idx) => (
                    <div key={idx} className="flex items-center space-x-3 bg-white rounded-lg p-3 border border-red-200">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="font-semibold text-gray-900">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 shadow-xl border-2 border-red-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Users className="w-8 h-8 text-red-600 mr-3" />
                  {siteData.legacy.partnershipsTitle}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {siteData.legacy.partnerships.map((partner, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-3 border border-red-200 text-center">
                      <span className="font-semibold text-gray-900 text-sm">{partner}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Prescription Upload & Expert Consultation Section */}
      <section id="consultation" className="py-20 bg-gradient-to-br from-red-600 to-red-700">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-12 text-white">
              <h2 className="text-4xl lg:text-5xl font-bold">
                {siteData.consultation.title}
              </h2>
              <p className="text-xl text-red-100">
                {siteData.consultation.subtitle}
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Consultation Form */}
              <div className="bg-white rounded-2xl p-8 shadow-2xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{siteData.consultation.formTitle}</h3>
                <form onSubmit={handleConsultationSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 font-semibold">Full Name *</Label>
                    <Input
                      id="name"
                      value={consultationForm.name}
                      onChange={(e) => setConsultationForm({ ...consultationForm, name: e.target.value })}
                      required
                      className="mt-2 h-12"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-semibold">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={consultationForm.email}
                      onChange={(e) => setConsultationForm({ ...consultationForm, email: e.target.value })}
                      required
                      className="mt-2 h-12"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-gray-700 font-semibold">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={consultationForm.phone}
                      onChange={(e) => setConsultationForm({ ...consultationForm, phone: e.target.value })}
                      required
                      className="mt-2 h-12"
                      placeholder="+1 234 567 8900"
                    />
                  </div>

                  <div>
                    <Label htmlFor="condition" className="text-gray-700 font-semibold">Medical Condition *</Label>
                    <Input
                      id="condition"
                      value={consultationForm.condition}
                      onChange={(e) => setConsultationForm({ ...consultationForm, condition: e.target.value })}
                      required
                      className="mt-2 h-12"
                      placeholder="e.g., Hepatitis C, Diabetes, HIV"
                    />
                  </div>

                  <div>
                    <Label htmlFor="prescription" className="text-gray-700 font-semibold">Upload Prescription *</Label>
                    <div className="mt-2 border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-red-400 transition-colors cursor-pointer">
                      <input
                        type="file"
                        id="prescription"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handlePrescriptionUpload}
                        className="hidden"
                      />
                      <label htmlFor="prescription" className="cursor-pointer flex flex-col items-center">
                        <Upload className="w-12 h-12 text-red-600 mb-3" />
                        <span className="font-semibold text-gray-900">Click to upload prescription</span>
                        <span className="text-sm text-gray-600 mt-1">PDF, JPG, PNG (Max 5MB)</span>
                        {prescriptionFile && (
                          <span className="mt-3 text-sm text-green-600 font-semibold">
                            ✓ {prescriptionFile.name}
                          </span>
                        )}
                      </label>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-700 font-semibold">Additional Information</Label>
                    <Textarea
                      id="message"
                      value={consultationForm.message}
                      onChange={(e) => setConsultationForm({ ...consultationForm, message: e.target.value })}
                      className="mt-2"
                      rows={4}
                      placeholder="Tell us about your requirements, current medications, etc."
                    />
                  </div>

                  <Button type="submit" disabled={isSubmitting} className="w-full bg-red-600 hover:bg-red-700 text-white py-4 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-70">
                    {isSubmitting ? 'Submitting Request...' : 'Submit for Expert Review'}
                  </Button>

                  <p className="text-xs text-gray-600 text-center">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Response within 4 hours | Consultation available 24/7
                  </p>
                </form>
              </div>

              {/* Expert Info */}
              <div className="space-y-6">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border-2 border-white/20 text-white">
                  <h3 className="text-2xl font-bold mb-6">{siteData.consultation.benefitsTitle}</h3>
                  <div className="space-y-4">
                    {siteData.consultation.benefits.map((type, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                        <span className="font-semibold">{type}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-2xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">{siteData.consultation.expertsTitle}</h3>
                  <div className="space-y-4">
                    {siteData.consultation.experts.map((expert, idx) => (
                      <div key={idx} className="border-l-4 border-red-600 pl-4 py-2">
                        <p className="font-bold text-gray-900">{expert.name}</p>
                        <p className="text-sm text-gray-600">{expert.qualification}</p>
                        <p className="text-sm text-red-600 font-semibold">{expert.specialty}</p>
                        <p className="text-xs text-gray-500">{expert.experience} experience</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Gateway Information */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <h2 className="text-4xl font-bold text-gray-900">
                {siteData.payment.title.split(' ').map((word, i) =>
                  word.toLowerCase().includes('payment') ? <span key={i} className="text-red-600">{word} </span> : word + ' '
                )}
              </h2>
              <p className="text-xl text-gray-600">
                {siteData.payment.subtitle}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 shadow-xl border-2 border-red-100">
                <CreditCard className="w-12 h-12 text-red-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">{siteData.payment.methodsTitle}</h3>
                <div className="space-y-2">
                  {siteData.payment.methods.map((method, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="text-gray-700">{method}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 shadow-xl border-2 border-red-100">
                <ShieldCheck className="w-12 h-12 text-red-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">{siteData.payment.securityTitle}</h3>
                <div className="space-y-2">
                  {siteData.payment.security.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 shadow-xl border-2 border-red-100">
                <DollarSign className="w-12 h-12 text-red-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">{siteData.payment.additionalTitle}</h3>
                <div className="space-y-2">
                  {siteData.payment.additional.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gradient-to-r from-green-50 to-white rounded-2xl p-8 border-2 border-green-200">
              <div className="flex items-center justify-center space-x-8">
                <ShieldCheck className="w-16 h-16 text-green-600" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {siteData.payment.certText}
                  </h3>
                  <p className="text-gray-600">
                    {siteData.payment.certDesc}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold">
              {siteData.finalCTA.title}
            </h2>
            <p className="text-xl text-red-100">
              {siteData.finalCTA.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => handleCTAClick(siteData.finalCTA.primaryButtonTarget || '#categories')} className="bg-white text-red-600 hover:bg-gray-100 px-10 py-4 text-lg rounded-xl font-bold shadow-2xl hover:scale-105 transition-all">
                {siteData.finalCTA.primaryButton}
              </Button>
              <Button onClick={() => handleCTAClick(siteData.finalCTA.secondaryButtonTarget || 'whatsapp')} className="bg-green-600 hover:bg-green-700 text-white px-10 py-4 text-lg rounded-xl font-bold shadow-2xl hover:scale-105 transition-all flex items-center justify-center space-x-3">
                <MessageCircle className="w-5 h-5" />
                <span>{siteData.finalCTA.secondaryButton}</span>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <FAQ
        faqs={siteData.faq || []}
        WhatsAppClick={handleWhatsApp}
      />

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">{siteData.header.logoLabel}</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">{siteData.footer.aboutTitle}</h3>
                  <p className="text-xs text-gray-400">{siteData.footer.aboutSubtitle}</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                {siteData.footer.aboutDesc}
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-4">{siteData.footer.quickLinksTitle}</h4>
              <ul className="space-y-2">
                {siteData.header.navLinks.map((link, idx) => (
                  <li key={idx}><button onClick={() => document.querySelector(link.target)?.scrollIntoView({ behavior: 'smooth' })} className="text-gray-400 hover:text-white text-sm transition-colors">{link.label}</button></li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">{siteData.footer.contactTitle}</h4>
              <ul className="space-y-4">
                <li className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-red-600" />
                  <span className="text-gray-400 text-sm">{siteData.contact.phone}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-400 text-sm">{siteData.footer.whatsappStatus || 'WhatsApp available'}</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">{siteData.footer.quickContactTitle || 'Quick Contact'}</h4>
              <div className="space-y-3">
                <Button onClick={() => handleCTAClick(siteData.footer.quickContactButtonTarget || 'whatsapp')} className="w-full bg-green-600 hover:bg-green-700 text-white">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {siteData.footer.quickContactButton || 'WhatsApp'}
                </Button>
                <p className="text-sm text-gray-400">{siteData.footer.quickContactDesc || 'Available 24/7 for consultations'}</p>
              </div>
            </div>
          </div>
          <div className="pt-12 border-t border-gray-800 text-center">
            <p className="text-gray-500 text-sm">{siteData.footer.copyright}</p>
          </div>
        </div>
      </footer>

      {/* Sticky WhatsApp Button */}
      <button
        onClick={handleWhatsApp}
        className="fixed bottom-8 right-8 bg-green-600 hover:bg-green-700 text-white p-5 rounded-full shadow-2xl hover:scale-110 transition-all z-50 flex items-center space-x-2 group"
      >
        <MessageCircle className="w-7 h-7" />
        <span className="hidden group-hover:inline-block pr-3 font-bold text-lg">Chat with Expert</span>
      </button>
    </div>
  );
};

export default PharmacyHomePage;